// components/Common/UserForm/AddUserModal.js
import React, { useMemo } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
// Removed Link import as we'll use Button for Close for consistent styling, can be reverted if Link specific style is needed.
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";

import { generateValidationSchema } from "../../../helpers/validationSchema";
import AddCommonForm from "../AddCommonForm/AddCommonForm"; // Default FormBody


const CommonAddModal = ({
  show,
  setShow,
  onSubmitHandler,
  labels,
  isAddOrEdit,
  selectedItemData,
  title, 
  size = "xl", 
  backdrop = true,
  fullscreen = false,
  FormBody = AddCommonForm,
  FormBodyProps = {},
  isDisabled,
  skipValidation = false
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";  
  
  const initialValues = useMemo(() => {
    // Start with default values derived from labels
    let baseValues = labels.reduce((acc, field) => {
      if (field.fieldName === 'question-list') { // For multi-select/list type fields
        acc[field.name] = field.defaultValue || [];
      } else if (field.fieldName === 'check-box') { // For checkbox fields
        acc[field.name] = field.defaultValue || false;
      }
      else {
        acc[field.name] = field.defaultValue || "";
      }
      return acc;
    }, {});

    const hasMobileBox = labels.some(label => label.fieldName === 'mobile-box' || label.type === 'mobile-box');
    if (hasMobileBox && !baseValues.hasOwnProperty('CountryCodeID')) {
      baseValues['CountryCodeID'] = "";
    }

    if (selectedItemData) {
      labels.forEach(field => {
        if (selectedItemData.hasOwnProperty(field.name)) {
          baseValues[field.name] = selectedItemData[field.name] ?? "";
        }
        else if (field.value && selectedItemData.hasOwnProperty(field.value)) {
          baseValues[field.name] = selectedItemData[field.value] ?? "";
        }
      });

      if (selectedItemData.hasOwnProperty('CountryCodeID')) {
        baseValues['CountryCodeID'] = selectedItemData['CountryCodeID'] ?? "";
      }
    }

    return baseValues;
  }, [labels, selectedItemData]); 

  // Generate validation schema only if not skipping validation
  const generatedValidationSchema = useMemo(() => {
    if (skipValidation) {
      return undefined;
    }
    return generateValidationSchema(labels, t, i18n);
  }, [labels, skipValidation, t, i18n]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={generatedValidationSchema}
      onSubmit={onSubmitHandler}
      enableReinitialize 
    >
      {(formikProps) => {
        const handleClose = () => {
          setShow(false);
          formikProps.resetForm(); 
        };
        
        // Determine if the button should be disabled based on Formik's state,
        // but only if we are NOT skipping validation.
        const formikDrivenDisabled = skipValidation
          ? false // If skipping validation, Formik's isValid/isDirty doesn't contribute to disabling
          : !(formikProps.isValid && formikProps.dirty); 

        const isButtonDisabled = formikDrivenDisabled || formikProps.isSubmitting || isDisabled;
        
        return (
        <Modal 
          isOpen={show} 
          backdrop={backdrop} 
          toggle={handleClose} 
          centered 
          size={size} 
          className={isRTL ? "rtl-modal" : ""}
          fullscreen={fullscreen}
        >
          <ModalHeader tag="div" toggle={handleClose} className="align-items-center">
             <h5 className="modal-title fw-semibold mb-0">{title}</h5>
          </ModalHeader>
          <ModalBody>
            <FormBody
              labels={labels}
              formikProps={formikProps}
              isRTL={isRTL}
              isAddOrEdit={isAddOrEdit}
              {...FormBodyProps}
            />
          </ModalBody>
          <ModalFooter className={""}>
            <Button color="light" onClick={handleClose} className={isRTL ? "ms-2" : "me-2"}>
              <i className="ri-close-line me-1 align-middle"></i> {t("Close", "Close")}
            </Button>
            <Button
              color="primary" 
              onClick={formikProps.handleSubmit}
              type="submit"
              disabled={isButtonDisabled}
            >
              <i className=" ri-save-line align-middle me-1 ms-1"></i>
              {t("Submit")}
            </Button>
          </ModalFooter>
        </Modal>);
      }}
    </Formik>
  );
};

export default CommonAddModal;